import React, { useState, useEffect } from 'react';
import { 
  generateDocument, 
  getAvailableDocuments, 
  DOCUMENT_TEMPLATES 
} from './LegalDocumentTemplates';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { genererPDFDocumentLegal, imprimerDocumentLegal } from '../services/legalDocumentsPdfService';
import './LegalDocumentsManager.css';

const LegalDocumentsManager = ({ client, user, onClientSelect }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  // Charger les documents du client
  useEffect(() => {
    if (client?.id) {
      loadClientDocuments();
    }
  }, [client?.id]);

  const loadClientDocuments = async () => {
    try {
      setIsLoading(true);
      const q = query(
        collection(db, 'legalDocuments'),
        where('clientId', '==', client.id),
        orderBy('metadata.createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ id: doc.id, ...doc.data() });
      });
      setDocuments(docs);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Récupérer les informations de la société depuis les paramètres
  const getCompanyInfo = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'company'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        return {
          name: data.companyName || 'OMAD Services',
          address: data.companyAddress || '30 bis Rue des Bondoires, 18230 Saint-Doulchard',
          phone: data.companyPhone || '06 67 72 73 30',
          email: data.companyEmail || 'contact@omadservices.com',
          siret: data.siret || '',
          rcs: data.rcs || 'RCS Bourges n° 922 592 662',
          capital: data.capital || '500,00 €'
        };
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres société:', error);
    }
    
    // Valeurs par défaut si les paramètres ne sont pas trouvés
    return {
      name: 'OMAD Services',
      address: '30 bis Rue des Bondoires, 18230 Saint-Doulchard',
      phone: '06 67 72 73 30',
      email: 'contact@omadservices.com',
      siret: '',
      rcs: 'RCS Bourges n° 922 592 662',
      capital: '500,00 €'
    };
  };

  // Générer un nouveau document
  const generateNewDocument = async (templateType) => {
    try {
      // Vérifier si un client est sélectionné
      if (!client || !client.id) {
        alert('Veuillez d\'abord sélectionner un client pour générer un document');
        return;
      }

      setIsLoading(true);
      
      // Récupérer les informations de la société
      const companyInfo = await getCompanyInfo();
      
      // Formater l'adresse du client (peut être un objet ou une chaîne)
      const formatClientAddress = (address) => {
        if (!address) return '';
        if (typeof address === 'string') return address;
        
        // Si c'est un objet, formater l'adresse
        const { street, city, postalCode, country } = address;
        const parts = [];
        if (street) parts.push(street);
        if (postalCode) parts.push(postalCode);
        if (city) parts.push(city);
        if (country) parts.push(country);
        return parts.join(', ');
      };

      const data = {
        client: {
          id: client.id,
          firstName: client.firstName || '',
          lastName: client.lastName || '',
          address: formatClientAddress(client.address),
          phone: client.phoneNumber || client.phone || '',
          email: client.email || ''
        },
        companyInfo: companyInfo
      };

      const content = generateDocument(templateType, data);
      
      const documentData = {
        clientId: client.id,
        documentType: templateType,
        documentVersion: '1.0',
        content: content,
        status: 'draft',
        metadata: {
          createdAt: serverTimestamp(),
          generatedBy: user?.uid || 'system',
          clientName: `${client.firstName || ''} ${client.lastName || ''}`.trim()
        }
      };

      const docRef = doc(collection(db, 'legalDocuments'));
      await setDoc(docRef, documentData);

      setSelectedDocument({ id: docRef.id, ...documentData });
      setDocumentContent(content);
      
      await loadClientDocuments();
      
    } catch (error) {
      console.error('Erreur lors de la génération du document:', error);
      alert('Erreur lors de la génération du document');
    } finally {
      setIsLoading(false);
    }
  };

  // Envoyer le document pour signature
  const sendForSignature = async (documentId) => {
    try {
      setIsLoading(true);
      const docRef = doc(db, 'legalDocuments', documentId);
      await updateDoc(docRef, {
        status: 'sent',
        'metadata.sentAt': serverTimestamp(),
        'metadata.sentBy': user?.uid
      });
      
      setShowSignatureModal(true);
      setSignatureData({ documentId, documentType: selectedDocument?.documentType });
      
      await loadClientDocuments();
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi pour signature:', error);
      alert('Erreur lors de l\'envoi pour signature');
    } finally {
      setIsLoading(false);
    }
  };

  // Afficher un document
  const viewDocument = (document) => {
    setSelectedDocument(document);
    setDocumentContent(document.content);
  };

  // Télécharger le document
  const downloadDocument = () => {
    const blob = new Blob([documentContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDocument?.documentType}_${client?.lastName}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Supprimer un document brouillon
  const deleteDocument = async (documentId, documentType) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le document "${getDocumentTypeName(documentType)}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      setIsLoading(true);
      await deleteDoc(doc(db, 'legalDocuments', documentId));
      
      // Si le document supprimé était celui affiché, le désélectionner
      if (selectedDocument?.id === documentId) {
        setSelectedDocument(null);
        setDocumentContent('');
      }
      
      // Recharger la liste des documents
      await loadClientDocuments();
      
      alert('Document supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      alert('Erreur lors de la suppression du document');
    } finally {
      setIsLoading(false);
    }
  };

  // Obtenir le statut du document avec badge coloré
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', color: 'gray' },
      sent: { label: 'Envoyé', color: 'blue' },
      signed: { label: 'Signé', color: 'green' },
      archived: { label: 'Archivé', color: 'purple' }
    };
    
    const config = statusConfig[status] || { label: status, color: 'gray' };
    return (
      <span className={`status-badge status-${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Obtenir le nom du type de document
  const getDocumentTypeName = (type) => {
    const types = {
      MANDAT: 'Contrat de Prestation de Mandat',
      CGS: 'Conditions Générales de Service',
      CONTRAT_TRAVAIL: 'Contrat de Travail',
      REGLEMENT_BENEFICIAIRES: 'Règlement Intérieur Bénéficiaires',
      REGLEMENT_INTERVENANTS: 'Règlement Intérieur Intervenants',
      ATTESTATION_FISCALE: 'Attestation Fiscale Annuelle'
    };
    return types[type] || type;
  };

  // Télécharger le document en PDF
  const downloadPDF = async (document) => {
    try {
      setIsLoading(true);
      await genererPDFDocumentLegal(document, client);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Imprimer le document
  const printDocument = async (document) => {
    try {
      setIsLoading(true);
      await imprimerDocumentLegal(document, client);
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      alert('Erreur lors de l\'impression: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // État pour la recherche et sélection de client
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSelector, setShowClientSelector] = useState(false);

  // Fonction pour charger les clients (à implémenter avec Firestore)
  const [availableClients, setAvailableClients] = useState([]);
  
  useEffect(() => {
    // Charger la liste des clients depuis Firestore
    const loadClients = async () => {
      try {
        const q = query(collection(db, 'clients'), orderBy('lastName'));
        const querySnapshot = await getDocs(q);
        const clientsList = [];
        querySnapshot.forEach((doc) => {
          clientsList.push({ id: doc.id, ...doc.data() });
        });
        setAvailableClients(clientsList);
      } catch (error) {
        console.error('Erreur lors du chargement des clients:', error);
      }
    };

    loadClients();
  }, []);

  // Filtrer les clients selon la recherche
  const filteredClients = availableClients.filter(client => 
    client.firstName?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.lastName?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.email?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Afficher l'interface selon qu'un client est sélectionné ou non
  if (!client || !client.id) {
    return (
      <div className="legal-documents-manager">
        <div className="documents-header">
          <h2>Documents Légaux</h2>
          <p>Gestion des documents contractuels et légaux avec signature électronique</p>
        </div>

        <div className="no-client-selected">
          <div className="placeholder-content">
            <h3>Sélectionnez un client</h3>
            <p>Choisissez un bénéficiaire pour accéder à ses documents légaux.</p>

            {/* Sélecteur de client */}
            <div className="client-selector">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Rechercher un client par nom, prénom ou email..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="search-input"
                  onFocus={() => setShowClientSelector(true)}
                />
                <span className="search-icon">🔍</span>
              </div>

              {showClientSelector && (
                <div className="client-list">
                  {filteredClients.length === 0 ? (
                    <div className="no-clients">
                      {clientSearch ? 'Aucun client trouvé' : 'Aucun client disponible'}
                    </div>
                  ) : (
                    <div className="clients-grid">
                      {filteredClients.slice(0, 10).map((clientItem) => (
                        <div
                          key={clientItem.id}
                          className="client-card"
                          onClick={() => {
                            if (onClientSelect) {
                              onClientSelect(clientItem);
                            }
                            setShowClientSelector(false);
                            setClientSearch('');
                          }}
                        >
                          <div className="client-avatar">
                            {clientItem.firstName?.[0]}{clientItem.lastName?.[0]}
                          </div>
                          <div className="client-info">
                            <strong>{clientItem.firstName} {clientItem.lastName}</strong>
                            {clientItem.email && (
                              <small>{clientItem.email}</small>
                            )}
                            {clientItem.phoneNumber && (
                              <small>{clientItem.phoneNumber}</small>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="placeholder-features">
              <div className="feature">
                <span className="feature-icon">⚡</span>
                <div>
                  <strong>Génération automatique</strong>
                  <p>Créez des documents légaux conformes en un clic</p>
                </div>
              </div>
              <div className="feature">
                <span className="feature-icon">✍️</span>
                <div>
                  <strong>Signature électronique</strong>
                  <p>Signature sécurisée avec traçabilité complète</p>
                </div>
              </div>
              <div className="feature">
                <span className="feature-icon">📊</span>
                <div>
                  <strong>Gestion des versions</strong>
                  <p>Historique complet des modifications</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overlay pour fermer le sélecteur */}
        {showClientSelector && (
          <div
            className="client-selector-overlay"
            onClick={() => setShowClientSelector(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="legal-documents-manager">
      <div className="documents-header">
        <h2>Documents Légaux - {client?.firstName} {client?.lastName}</h2>
        <p>Gestion des documents contractuels et légaux avec signature électronique</p>
      </div>

      <div className="documents-layout">
        {/* Sidebar - Liste des documents */}
        <div className="documents-sidebar">
          <div className="sidebar-section">
            <h3>Documents disponibles</h3>
            <div className="document-types">
              {getAvailableDocuments().map((docType) => (
                <button
                  key={docType.id}
                  className="document-type-btn"
                  onClick={() => generateNewDocument(docType.id)}
                  disabled={isLoading}
                >
                  <span className="document-icon">📄</span>
                  <div className="document-info">
                    <strong>{docType.label}</strong>
                    <small>{docType.description}</small>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Documents existants</h3>
            {isLoading ? (
              <div className="loading">Chargement...</div>
            ) : documents.length === 0 ? (
              <div className="no-documents">
                Aucun document généré pour ce client
              </div>
            ) : (
              <div className="documents-list">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className={`document-item ${selectedDocument?.id === doc.id ? 'active' : ''}`}
                  >
                    <div className="document-header">
                      <span 
                        className="document-name"
                        onClick={() => viewDocument(doc)}
                        style={{ cursor: 'pointer', flex: 1 }}
                      >
                        {getDocumentTypeName(doc.documentType)}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getStatusBadge(doc.status)}
                        {doc.status === 'draft' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteDocument(doc.id, doc.documentType);
                            }}
                            className="btn-secondary"
                            style={{ 
                              padding: '4px 8px', 
                              fontSize: '0.7rem',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            title="Supprimer ce brouillon"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="document-meta">
                      <small>
                        Version {doc.documentVersion} • 
                        {doc.metadata?.createdAt?.toDate?.()?.toLocaleDateString('fr-FR') || 'Date inconnue'}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main content - Visualisation du document */}
        <div className="document-viewer">
          {selectedDocument ? (
            <>
              <div className="viewer-header">
                <div className="document-info">
                  <h3>{getDocumentTypeName(selectedDocument.documentType)}</h3>
                  <div className="document-actions">
                    {getStatusBadge(selectedDocument.status)}
                    <button 
                      className="btn-secondary"
                      onClick={() => downloadPDF(selectedDocument)}
                      title="Télécharger en PDF"
                    >
                      📥 PDF
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={() => printDocument(selectedDocument)}
                      title="Imprimer le document"
                    >
                      🖨️ Imprimer
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={downloadDocument}
                      title="Télécharger en HTML"
                    >
                      📄 HTML
                    </button>
                    {selectedDocument.status === 'draft' && (
                      <>
                        <button 
                          className="btn-primary"
                          onClick={() => sendForSignature(selectedDocument.id)}
                          disabled={isLoading}
                        >
                          ✍️ Envoyer pour signature
                        </button>
                        <button 
                          className="btn-secondary"
                          onClick={() => deleteDocument(selectedDocument.id, selectedDocument.documentType)}
                          disabled={isLoading}
                          style={{ 
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          🗑️ Supprimer
                        </button>
                      </>
                    )}
                    {selectedDocument.status === 'sent' && (
                      <button className="btn-warning">
                        ⏰ En attente de signature
                      </button>
                    )}
                    {selectedDocument.status === 'signed' && (
                      <button className="btn-success">
                        ✅ Signé le {selectedDocument.metadata?.signedAt?.toDate?.()?.toLocaleDateString('fr-FR')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="document-content">
                <iframe
                  srcDoc={documentContent}
                  title="Document légal"
                  className="document-iframe"
                  sandbox="allow-same-origin"
                />
              </div>
            </>
          ) : (
            <div className="no-document-selected">
              <div className="placeholder-content">
                <h3>Sélectionnez un document</h3>
                <p>Choisissez un document dans la liste ou générez-en un nouveau pour commencer.</p>
                <div className="placeholder-features">
                  <div className="feature">
                    <span className="feature-icon">⚡</span>
                    <div>
                      <strong>Génération automatique</strong>
                      <p>Créez des documents légaux conformes en un clic</p>
                    </div>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">✍️</span>
                    <div>
                      <strong>Signature électronique</strong>
                      <p>Signature sécurisée avec traçabilité complète</p>
                    </div>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">📊</span>
                    <div>
                      <strong>Gestion des versions</strong>
                      <p>Historique complet des modifications</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de signature */}
      {showSignatureModal && (
        <ElectronicSignatureModal
          documentData={signatureData}
          client={client}
          onClose={() => setShowSignatureModal(false)}
          onSuccess={() => {
            setShowSignatureModal(false);
            loadClientDocuments();
          }}
        />
      )}
    </div>
  );
};

// Composant modal pour la signature électronique
const ElectronicSignatureModal = ({ documentData, client, onClose, onSuccess }) => {
  const [signature, setSignature] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [canvasRef] = useState(React.createRef());

  const handleSignatureComplete = async (signatureData) => {
    try {
      setIsSigning(true);
      
      // Enregistrer la signature dans Firestore
      const docRef = doc(db, 'legalDocuments', documentData.documentId);
      await updateDoc(docRef, {
        status: 'signed',
        signature: {
          clientSignature: signatureData,
          clientName: `${client.firstName} ${client.lastName}`,
          signatureDate: serverTimestamp(),
          ipAddress: 'captured' // À remplacer par l'IP réelle en production
        },
        'metadata.signedAt': serverTimestamp()
      });

      onSuccess();
      
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la signature:', error);
      alert('Erreur lors de l\'enregistrement de la signature');
    } finally {
      setIsSigning(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="signature-modal">
        <div className="modal-header">
          <h3>Signature électronique</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-content">
          <p>
            <strong>Client :</strong> {client.firstName} {client.lastName}<br/>
            <strong>Document :</strong> {documentData.documentType}
          </p>
          
          <div className="signature-instructions">
            <p>Veuillez signer dans la zone ci-dessous :</p>
          </div>
          
          <div className="signature-canvas-container">
            <canvas
              ref={canvasRef}
              width={400}
              height={200}
              className="signature-canvas"
              onMouseDown={(e) => {
                // Implémentation basique du canvas de signature
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                ctx.beginPath();
                ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                
                const draw = (e) => {
                  ctx.lineTo(e.offsetX, e.offsetY);
                  ctx.stroke();
                };
                
                canvas.addEventListener('mousemove', draw);
                canvas.addEventListener('mouseup', () => {
                  canvas.removeEventListener('mousemove', draw);
                  setSignature(canvas.toDataURL());
                });
              }}
            />
            <button 
              className="clear-signature"
              onClick={() => {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                setSignature('');
              }}
            >
              Effacer la signature
            </button>
          </div>
          
          <div className="signature-actions">
            <button 
              className="btn-secondary"
              onClick={onClose}
            >
              Annuler
            </button>
            <button 
              className="btn-primary"
              onClick={() => handleSignatureComplete(signature)}
              disabled={!signature || isSigning}
            >
              {isSigning ? 'Signature en cours...' : 'Confirmer la signature'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentsManager;
